import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { gatewayContagemAPI } from '../../../API/gatewayContagemAPI';
import { getRowProfileCode, rowNeedsClient, rowNeedsStores } from '../../Tables/CreateUsersTable';

const FIELD_HEIGHT = 32;
const GRID_TEMPLATE = '1fr 1fr 100px 220px';

export const getClientRowError = (row, profileOptions) => {
  if (!rowNeedsClient(row, profileOptions)) return null;
  if (!row.clientId) return 'required-client';
  return null;
};

export const isClientStepValid = (rows, profileOptions) =>
  rows.every((row) => row.sent || !getClientRowError(row, profileOptions));

// ── Cliente/Lojas compound cell (perfil USER) ───────────────────────────────

// eslint-disable-next-line react/prop-types
const ClientStoresCell = ({ row, clients, storesByClient, onLoadStores, onChange, hasError, loadingClientId, isClientAdminCaller }) => {
  const { t } = useTranslation('users');
  const [anchor, setAnchor] = useState(null);

  const stores = storesByClient[row.clientId] ?? [];
  const isLoadingStores = loadingClientId === row.clientId;
  const client = clients.find((c) => c.id === row.clientId);

  useEffect(() => {
    if (isClientAdminCaller && row.clientId && !storesByClient[row.clientId] && !isLoadingStores) {
      onLoadStores(row.clientId);
    }
  }, [isClientAdminCaller, row.clientId, storesByClient, isLoadingStores, onLoadStores]);

  const handlePickClient = (clientId) => {
    onChange({ clientId, storeIds: [] });
    if (!storesByClient[clientId]) onLoadStores(clientId);
  };

  const toggleStore = (storeId) => {
    const allIds = stores.map((s) => s.id);
    const current = row.storeIds.length === 0 ? allIds : row.storeIds;
    const next = current.includes(storeId) ? current.filter((id) => id !== storeId) : [...current, storeId];
    onChange({ storeIds: next.length === allIds.length ? [] : next });
  };

  const summary = !client
    ? t('invite.bulkSelectClient')
    : row.storeIds.length === 0
    ? `${client.name} · ${t('invite.bulkAllStores')}`
    : `${client.name} · ${row.storeIds.length} ${t('invite.bulkStoresSelected')}`;

  const storesSection = isLoadingStores ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
      <CircularProgress size={20} />
    </Box>
  ) : (
    <>
      <MenuItem dense onClick={() => onChange({ storeIds: [] })} sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
        {t('invite.bulkSelectAll')}
      </MenuItem>
      {stores.map((s) => (
        <MenuItem key={s.id} dense onClick={() => toggleStore(s.id)} sx={{ fontSize: 12, py: 0.25 }}>
          <Checkbox size="small" checked={row.storeIds.length === 0 || row.storeIds.includes(s.id)} sx={{ p: 0.5 }} />
          {s.name}
        </MenuItem>
      ))}
    </>
  );

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ArrowDownIcon sx={{ fontSize: 16, flexShrink: 0 }} />}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 0.5,
          border: '1px solid',
          borderColor: hasError ? 'error.main' : 'divider',
          borderRadius: '8px',
          px: 1,
          height: FIELD_HEIGHT,
          fontSize: 13,
          fontWeight: 400,
          textTransform: 'none',
          color: 'text.primary',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</Box>
      </Button>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ minWidth: 220, maxWidth: 280, py: 0.5 }}>
          {isClientAdminCaller ? (
            storesSection
          ) : (
            <>
              <List dense disablePadding>
                {clients.map((c) => (
                  <ListItemButton key={c.id} selected={c.id === row.clientId} onClick={() => handlePickClient(c.id)} sx={{ py: 0.5 }}>
                    <ListItemText primaryTypographyProps={{ fontSize: 13 }} primary={c.name} />
                  </ListItemButton>
                ))}
              </List>
              {client && (
                <>
                  <Divider />
                  {storesSection}
                </>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

// ── Cliente-só cell (perfil CLIENT_ADMIN) ───────────────────────────────────

// eslint-disable-next-line react/prop-types
const ClientOnlyCell = ({ row, clients, onChange, hasError, isClientAdminCaller }) => {
  const { t } = useTranslation('users');

  if (isClientAdminCaller) {
    const client = clients.find((c) => c.id === row.clientId);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ height: FIELD_HEIGHT, display: 'flex', alignItems: 'center', px: 1, fontSize: 13, color: 'text.primary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {client?.name ?? ''}
        </Box>
        <Tooltip title={t('invite.hints.clientAdminAllStoresInfo')}>
          <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Select
        size="small" displayEmpty value={row.clientId}
        onChange={(e) => onChange({ clientId: e.target.value })}
        sx={{
          height: FIELD_HEIGHT,
          fontSize: 13,
          flex: 1,
          '& .MuiOutlinedInput-notchedOutline': hasError ? { borderColor: 'error.main' } : {},
        }}
      >
        <MenuItem value="" disabled sx={{ fontSize: 13 }}>{t('invite.placeholders.selectClient')}</MenuItem>
        {clients.map((c) => <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.name}</MenuItem>)}
      </Select>
      <Tooltip title={t('invite.hints.clientAdminAllStoresInfo')}>
        <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
      </Tooltip>
    </Box>
  );
};

// ── Step ─────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
const BulkClientStoresStep = ({ rows, onRowsChange, profileOptions, clients, attemptedSubmit, isClientAdminCaller }) => {
  const { t } = useTranslation('users');
  const [storesByClient, setStoresByClient] = useState({});
  const [loadingClientId, setLoadingClientId] = useState(null);

  const loadStores = async (clientId) => {
    setLoadingClientId(clientId);
    try {
      const res = await gatewayContagemAPI.listStoresByClient(clientId);
      setStoresByClient((prev) => ({ ...prev, [clientId]: Array.isArray(res?.value) ? res.value : [] }));
    } catch {
      setStoresByClient((prev) => ({ ...prev, [clientId]: [] }));
    } finally {
      setLoadingClientId((current) => (current === clientId ? null : current));
    }
  };

  const updateRow = (key, patch) => {
    onRowsChange(rows.map((r) => (r.key === key ? { ...r, ...patch, serverError: '' } : r)));
  };

  const relevantRows = rows.filter((row) => rowNeedsClient(row, profileOptions));

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, px: 0.5, fontSize: 11, color: 'text.disabled', textTransform: 'uppercase' }}>
        <Box>{t('invite.fields.name')}</Box>
        <Box>{t('invite.fields.email')}</Box>
        <Box>{t('invite.fields.profile')}</Box>
        <Box>{t('invite.stepClient')}</Box>
      </Box>

      {relevantRows.map((row) => {
        if (row.sent) {
          return (
            <Box
              key={row.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, height: FIELD_HEIGHT, bgcolor: '#f0fdf4', borderRadius: '8px', fontSize: 13, color: 'success.dark' }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
              {row.email}
            </Box>
          );
        }

        const clientError = attemptedSubmit && getClientRowError(row, profileOptions);
        return (
          <Box key={row.key} sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, alignItems: 'center' }}>
            <Box sx={{ height: FIELD_HEIGHT, display: 'flex', alignItems: 'center', px: 1, fontSize: 13, color: 'text.disabled', bgcolor: '#f7f7f8', borderRadius: '8px' }}>{row.name}</Box>
            <Box sx={{ height: FIELD_HEIGHT, display: 'flex', alignItems: 'center', px: 1, fontSize: 13, color: 'text.disabled', bgcolor: '#f7f7f8', borderRadius: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email}</Box>
            <Box sx={{ height: FIELD_HEIGHT, display: 'flex', alignItems: 'center', px: 1, fontSize: 13, color: 'text.disabled', bgcolor: '#f7f7f8', borderRadius: '8px' }}>{getRowProfileCode(row, profileOptions)}</Box>
            {rowNeedsStores(row, profileOptions) ? (
              <ClientStoresCell
                row={row} clients={clients} storesByClient={storesByClient}
                onLoadStores={loadStores} onChange={(patch) => updateRow(row.key, patch)}
                hasError={Boolean(clientError)} loadingClientId={loadingClientId}
                isClientAdminCaller={isClientAdminCaller}
              />
            ) : (
              <ClientOnlyCell row={row} clients={clients} onChange={(patch) => updateRow(row.key, patch)} hasError={Boolean(clientError)} isClientAdminCaller={isClientAdminCaller} />
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default BulkClientStoresStep;
